import { addToast } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { uploadCohortRangesTemplate } from '../../../../api/requests/tuitionContinuityTariff';
import UploadModal, { IInput } from '../../../../components/modals/UploadModal';
import { BulkRowError } from '../../../../types/tuitionContinuityTrariff';

interface Props {
  isOpen: boolean;
  periodId: number;
  schoolId: number;
  onClose: () => void;
  onUploaded: () => void;
}

export default function BulkTariffUploadModal({
  isOpen,
  periodId,
  schoolId,
  onClose,
  onUploaded,
}: Props) {
  const { t } = useTranslation();
  const prefix = 'tuitionContinuityTariff.cohort';
  const [errors, setErrors] = useState<BulkRowError[]>([]);

  const texts = useMemo(
    () => ({
      title: t(`${prefix}.uploadModalTitle`) || 'Carga masiva',
      subtitle: t(`${prefix}.uploadModalSubtitle`) || '',
      fileLabel: t(`${prefix}.uploadFileLabel`) || 'Archivo',
      uploadBtn: t(`${prefix}.uploadBtn`) || 'Subir',
      fileRequired: t(`${prefix}.uploadFileRequired`) || 'Archivo requerido',
      ok: t(`${prefix}.uploadOk`) || 'Carga aplicada',
      genericError: t('common.errors.save') || 'Error',
      errorsTitle: t(`${prefix}.uploadErrorsTitle`) || 'Errores:',
      row: t(`${prefix}.row`) || 'Fila',
    }),
    [t],
  );

  const submitFunction = useCallback(
    async (values: IInput) => {
      setErrors([]);
      const file = values.file as File;
      const { data, error } = await uploadCohortRangesTemplate(
        periodId,
        schoolId,
        file,
      );
      if (error) {
        addToast({ icon: 'error', color: 'danger', text: texts.genericError });
        return;
      }
      if (data && data.errors && data.errors.length > 0) {
        setErrors(data.errors);
        return;
      }
      addToast({ icon: 'success', color: 'success', text: texts.ok });
      onUploaded();
      onClose();
    },
    [periodId, schoolId, texts, onUploaded, onClose],
  );

  return (
    <UploadModal
      isOpen={isOpen}
      onClose={onClose}
      title={texts.title}
      subtitle={texts.subtitle}
      submitFunction={submitFunction}
    >
      <UploadModal.Input
        name="file"
        label={texts.fileLabel}
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        btnText={texts.uploadBtn}
        requiredMsgError={texts.fileRequired}
      />
      {errors.length > 0 && (
        <div className="mt-3" style={{ maxHeight: 200, overflowY: 'auto' }}>
          <p className="text-danger fw-600 mb-1">{texts.errorsTitle}</p>
          <ul className="text-danger fs-14">
            {errors.map((e, i) => (
              <li key={i}>
                {e.rowNumber > 0 ? `${texts.row} ${e.rowNumber}: ` : ''}
                {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </UploadModal>
  );
}
